import { useState, useEffect, useCallback, useRef } from 'react';
import { logout } from '@teetsh/app/src/queries/user/useLogout';

interface StreamOptions {
  onComplete?: () => void;
}

interface UseSSEStreamResult {
  data: string;
  error: string | null;
  isLoading: boolean;
  startStream: (params: any) => void;
  stopStream: () => void;
  reset: () => void;
}

const handleStreamError = (status: number) => {
  if (status === 503) {
    const maintenanceUrl = `${import.meta.env.VITE_APP_URL}/maintenance`;
    if (window.location.href !== maintenanceUrl) {
      window.location.href = maintenanceUrl;
    }
    return;
  }
  if (status === 401) {
    logout();
    const loginUrl = `${import.meta.env.VITE_APP_URL}/login`;
    if (window.location.href !== loginUrl) {
      window.location.href = loginUrl;
    }
    return;
  }
  if (status === 429) {
    return 'quota_exceeded';
  }
  return;
};

export function useSSEStream(
  path: string,
  options: StreamOptions = {},
): UseSSEStreamResult {
  const { onComplete = () => {} } = options;

  const [data, setData] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const abortControllerRef = useRef<AbortController | null>(null);
  const readerRef = useRef<ReadableStreamDefaultReader<Uint8Array> | null>(
    null,
  );
  const onCompleteRef = useRef(onComplete);

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  const stopStream = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    if (readerRef.current) {
      readerRef.current.cancel();
      readerRef.current = null;
    }
    setIsLoading(false);
    onCompleteRef.current();
  }, []);

  const reset = useCallback(() => {
    stopStream();
    setData('');
    setError('');
    setIsLoading(false);
    onCompleteRef.current();
  }, [stopStream]);

  const startStream = useCallback(
    async (body: any) => {
      // Clean up any existing stream
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (readerRef.current) {
        readerRef.current.cancel();
      }

      setIsLoading(true);
      setData('');
      setError(null);

      const baseURL = import.meta.env.VITE_API_URL;
      const url = new URL(path, baseURL);

      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };

      const token = localStorage?.getItem('id_token') || '';
      const shareToken = (window as any)?.shareToken;
      if (shareToken) {
        headers.Authorization = shareToken;
      } else if (token !== '') {
        headers.Authorization = `Bearer ${token}`;
      }

      // Add mock AI header for e2e tests
      if ((window as any).mockAI) {
        headers['X-Mock-AI'] = 'true';
      }

      // Create abort controller for cancellation
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      try {
        const response = await fetch(url.toString(), {
          method: 'POST',
          headers,
          body: JSON.stringify(body),
          credentials: 'include',
          signal: abortController.signal,
        });

        if (!response.ok) {
          const errorResult = handleStreamError(response.status);
          if (errorResult) {
            setError(errorResult);
          } else {
            setError('An error occurred');
          }
          setIsLoading(false);
          return;
        }

        // Start reading the stream
        const reader = response.body?.getReader();
        if (!reader) {
          setError('Stream not available');
          setIsLoading(false);
          return;
        }
        readerRef.current = reader;

        const decoder = new TextDecoder();
        let buffer = '';

        // Read stream chunks
        while (true) {
          try {
            const { done, value } = await reader.read();
            if (done) {
              stopStream();
              break;
            }

            // Decode chunk and add to buffer
            buffer += decoder.decode(value, { stream: true });

            // Process complete SSE messages
            const lines = buffer.split('\n');
            buffer = lines.pop() || ''; // Keep incomplete line in buffer

            for (const line of lines) {
              if (line.startsWith('data: ')) {
                const lineData = line.slice(6).trim();
                if (lineData === '[DONE]') {
                  stopStream();
                  return;
                }

                try {
                  const parsed = JSON.parse(lineData);

                  if (parsed.done) {
                    stopStream();
                    return;
                  }

                  if (parsed.error) {
                    setError(parsed.error);
                    stopStream();
                    return;
                  }

                  // Handle different response formats
                  if (parsed.text) {
                    setData(prev => prev + parsed.text);
                  } else if (parsed.corrected_text) {
                    setData(prev => prev + parsed.corrected_text);
                  }
                } catch (e) {
                  // eslint-disable-next-line no-console
                  console.error('Failed to parse SSE JSON:', e, lineData);
                }
              }
            }
          } catch (err: any) {
            if (err.name === 'AbortError') {
              // Stream was aborted intentionally
              break;
            }
            // eslint-disable-next-line no-console
            console.error('Stream reading error:', err);
            setError('Stream reading error');
            stopStream();
            break;
          }
        }
      } catch (err: any) {
        if (err.name !== 'AbortError') {
          // eslint-disable-next-line no-console
          console.error('Stream error:', err);
          setError('An error occurred');
        }
        setIsLoading(false);
      }
    },
    [path, stopStream],
  );

  // Cleanup on unmount
  useEffect(
    () => () => {
      stopStream();
    },
    [stopStream],
  );

  return {
    data,
    error,
    isLoading,
    startStream,
    stopStream,
    reset,
  };
}
